import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import SellerPlanPaybackController from './SellerPlanPayback';
import SellerPlanPaybackCard from './SellerPlanPaybackCard';
import { PAYBACK_CHANGED_EVENT, createPaybackClient } from './sellerPlanPayback';
import { SCENARIOS, createScriptedFetch } from './sellerPlanPaybackScenarios';
import './PlanBillingPage.css';
import './SellerPlanPayback.css';

const HARNESS_API = 'https://harness.invalid';
const LOG_LIMIT = 20;

/**
 * bktestpage harness for the seller plan payback popup and card (Phase 4a). Scripted scenarios
 * drive the real controller and card through a fake API; "Live" uses the real API for the
 * signed-in channel. Not linked from any customer page.
 */
export default function SellerPlanPaybackHarness() {
  const auth = useAuth();
  const [scenario, setScenario] = useState('pending');
  const [run, setRun] = useState(0);
  const [channel, setChannel] = useState('A');
  const [log, setLog] = useState([]);
  const [controller, setController] = useState(null);
  const [embedded, setEmbedded] = useState(null);

  const live = scenario === 'live';
  const addLog = useCallback(entry => setLog(previous => [...previous.slice(-(LOG_LIMIT - 1)), entry]), []);

  const scripted = useMemo(
    () => (live ? null : createScriptedFetch(scenario, { channel, onCall: addLog })),
    // `run` rebuilds the scripted server state; `channel` is the cross-channel scenario's switch.
    [live, scenario, channel, run, addLog],
  );
  const client = useMemo(
    () => (scripted ? createPaybackClient({ fetch: scripted.fetch, apiUrl: HARNESS_API }) : undefined),
    [scripted],
  );
  const channelId = scripted ? `harness-channel-${channel}` : undefined;

  // The card inside the billing-page layout takes the raw `data.payback`, like PlanBillingPage would.
  const readEmbedded = useCallback(async () => {
    const fetcher = scripted ? scripted.fetch : auth.fetch;
    const base = scripted ? HARNESS_API : auth.API_URL;
    try {
      const response = await fetcher(`${base}/v1/seller/payments/plan-billing?limit=1&offset=0`);
      const body = await response.json();
      return response.ok ? (body?.data?.payback ?? null) : null;
    } catch {
      return null;
    }
  }, [scripted, auth.fetch, auth.API_URL]);

  useEffect(() => {
    let current = true;
    const load = () => readEmbedded().then(payback => { if (current) setEmbedded(payback); });
    setEmbedded(null);
    load();
    window.addEventListener(PAYBACK_CHANGED_EVENT, load);
    return () => {
      current = false;
      window.removeEventListener(PAYBACK_CHANGED_EVENT, load);
    };
  }, [readEmbedded]);

  const choose = key => {
    setScenario(key);
    setChannel('A');
    setLog([]);
    setController(null);
    setRun(value => value + 1);
  };

  const snapshot = JSON.stringify({
    scenario,
    channel: scripted ? channel : '(live: signed-in channel)',
    controller,
    billingPageEmbeddedInput: embedded,
    requests: log,
  }, null, 2);

  return (
    <div className="sppb-harness">
      <div className="sppb-harness-controls">
        <fieldset>
          <legend>Scenario</legend>
          {SCENARIOS.map(item => (
            <label key={item.key}>
              <input
                type="radio"
                name="sppb-scenario"
                value={item.key}
                checked={scenario === item.key}
                onChange={() => choose(item.key)}
              />{' '}
              {item.label}
              <small>{item.hint}</small>
            </label>
          ))}
        </fieldset>
        <div className="sppb-harness-buttons">
          <button type="button" className="button secondary" onClick={() => choose(scenario)}>
            Reset scenario
          </button>
          <button
            type="button"
            className="button secondary"
            onClick={() => window.dispatchEvent(new CustomEvent(PAYBACK_CHANGED_EVENT))}
          >
            Simulate returning to the tab (re-read)
          </button>
          {scenario === 'crossChannel' && (
            <button
              type="button"
              className="button secondary"
              disabled={channel === 'B'}
              onClick={() => setChannel('B')}
            >
              Switch to channel B
            </button>
          )}
        </div>
      </div>

      <div className="sppb-harness-surfaces">
        <div className="sppb-harness-surface">
          <h3>Popup controller (dashboard mount)</h3>
          <SellerPlanPaybackController
            key={`controller-${scenario}-${run}`}
            client={client}
            channelId={channelId}
            onDebug={setController}
          />
          <p className="sppb-harness-empty">
            {controller?.popupOpen
              ? 'The popup is open.'
              : 'No popup open. It appears as an overlay when the server grants a lease.'}
          </p>
        </div>

        <div className="sppb-harness-surface">
          <h3>Lasting card, own mount (legacy plan page)</h3>
          <SellerPlanPaybackCard
            key={`card-${scenario}-${run}`}
            client={client}
            channelId={channelId}
          />
          <p className="sppb-harness-empty">Nothing above means the card renders nothing for this snapshot.</p>
        </div>

        <div className="sppb-harness-surface">
          <h3>Lasting card inside the billing page layout</h3>
          <div className="plan-billing-page">
            <SellerPlanPaybackCard payback={embedded} />
          </div>
          <p className="sppb-harness-empty">
            Same server snapshot, passed in as the billing read model would. Nothing above means nothing renders.
          </p>
        </div>

        <div className="sppb-harness-surface">
          <h3>Debug snapshot (no lease tokens)</h3>
          <pre className="sppb-harness-debug">{snapshot}</pre>
        </div>
      </div>
    </div>
  );
}
